import { withScope } from '@sentry/core'
import * as Sentry from '@sentry/react'
import { lokiLogger } from 'src/services/lokiLogger'

export interface ErrorLoggerOptions {
  context?: { [key: string]: any }
  source?: string
  component?: string
  skipConsole?: boolean
  skipSentry?: boolean
  skipLoki?: boolean
}

export interface InvocationInfo {
  callerLine: string
  stackContext: string
  fullInvocationStack: string
}

/**
 * Enhanced error logging utility that captures both original errors and invocation context
 * This function provides comprehensive error tracking by:
 * 1. Capturing where the error originally occurred (original stack trace)
 * 2. Capturing where the error logging was invoked (invocation stack trace)
 * 3. Logging to both console and Sentry with rich context
 * 4. Providing readable error information for debugging
 */
export function logErrorWithContext(error: string | Error | any, options: ErrorLoggerOptions = {}): void {
  const { context = {}, source = 'unknown', component = 'unknown', skipConsole = false, skipSentry = false, skipLoki = false } = options

  // Extract error message for display and logging
  let errorMessage: string
  if (typeof error === 'string') {
    errorMessage = error
  } else if (error instanceof Error) {
    errorMessage = error.message
  } else {
    errorMessage = error?.message || 'Something went wrong'
  }

  // Skip schema errors and other known non-critical errors
  const schemaError = "Failed to execute 'transaction' on 'IDBDatabase'"
  if (errorMessage === schemaError) {
    return
  }

  // Capture the current call stack to see where logErrorWithContext was invoked
  const invocationStack = new Error('Error logging invocation')

  // Determine the error object to report
  let errorToReport: Error
  if (error instanceof Error) {
    // Use the original error object to preserve its stack trace
    errorToReport = error
  } else {
    // Create a new error with the extracted message
    errorToReport = new Error(errorMessage)
  }

  // Extract useful information from the invocation stack
  const getInvocationInfo = (stackTrace: string): InvocationInfo => {
    const lines = stackTrace.split('\n')
    // Skip the first few lines which are internal error logging calls
    const relevantLines = lines.slice(3, 8) // Get 5 lines of context

    // Try to find the actual caller (not from error logging utilities)
    const callerLine = lines.find(
      (line) =>
        line.includes('.tsx') &&
        !line.includes('errorLogger') &&
        !line.includes('ErrorContext') &&
        !line.includes('SnackContext') &&
        !line.includes('useErrorData') &&
        !line.includes('useSnackData')
    )

    return {
      callerLine: callerLine?.trim() || 'Unknown caller',
      stackContext: relevantLines.join('\n'),
      fullInvocationStack: stackTrace
    }
  }

  const invocationInfo = getInvocationInfo(invocationStack.stack || '')

  // Console logging with rich context
  if (!skipConsole) {
    console.group(`🚨 Error in ${component} (${source})`)
    console.error('Error Message:', errorMessage)
    console.error('Original Error:', error)
    console.error('Called From:', invocationInfo.callerLine)
    console.error('Context:', context)
    console.error('Full Invocation Stack:', invocationInfo.fullInvocationStack)
    if (errorToReport.stack) {
      console.error('Original Stack Trace:', errorToReport.stack)
    }
    console.groupEnd()
  }

  // Loki logging
  if (!skipLoki) {
    try {
      lokiLogger.logClientError(errorMessage, component, {
        source,
        ...context,
        callerLine: invocationInfo.callerLine,
        pathname: window.location.pathname
      })
    } catch (lokiError) {
      console.error('Failed to report error to Loki:', lokiError)
    }
  }

  // Sentry logging with comprehensive context
  if (!skipSentry) {
    try {
      withScope((scope) => {
        // Add tags for filtering in Sentry dashboard
        scope.setTag('errorSource', source)
        scope.setTag('component', component)
        scope.setTag('errorType', typeof error)

        // Add the invocation information - where the error logging was called
        scope.setContext('invocation', {
          caller: invocationInfo.callerLine,
          stackContext: invocationInfo.stackContext,
          timestamp: new Date().toISOString(),
          source,
          component
        })

        // Add user-provided context
        if (Object.keys(context).length > 0) {
          scope.setContext('errorContext', context)
        }

        // Add current page context
        scope.setContext('page', {
          pathname: window.location.pathname,
          href: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        })

        // Add error details as extra information
        scope.setExtra('originalError', error)
        scope.setExtra('errorMessage', errorMessage)
        scope.setExtra('fullInvocationStack', invocationInfo.fullInvocationStack)

        // If the original error doesn't have a good stack trace, use the invocation stack
        if (!errorToReport.stack || errorToReport.stack === `Error: ${errorMessage}`) {
          scope.setExtra('enhancedStack', invocationInfo.fullInvocationStack)
        }

        // Capture the exception in Sentry
        Sentry.captureException(errorToReport)
      })
    } catch (sentryError) {
      console.error('Failed to report error to Sentry:', sentryError)
    }
  }
}
